import type { Centimetres, CentimetresDelta } from '~/types/units'

/**
 * Circumference repeat quality (D-016, D-045, D-046).
 *
 * Every recorded site uses exactly three readings, saves the median, and warns
 * when the spread exceeds the configured threshold. Quality is computed and
 * stored PER SITE — never as one session-wide value.
 *
 * Pure: no clock, no storage, no formatting.
 */

export const READINGS_PER_SITE = 3

/** Default maximum spread before a retake warning (D-016). Configurable. */
export const DEFAULT_SPREAD_THRESHOLD_CM = 0.7 as Centimetres

export type SiteQualityIssue = 'wrongReadingCount' | 'nonFiniteReading' | 'nonPositiveReading'

export interface SiteQuality {
  /** All three readings, retained in entry order — never discarded (D-016). */
  readonly readings: readonly Centimetres[]
  readonly medianCm: Centimetres
  readonly spreadCm: CentimetresDelta
  readonly exceedsThreshold: boolean
  readonly thresholdUsedCm: Centimetres
}

export type SiteQualityResult =
  | { readonly status: 'ok'; readonly quality: SiteQuality }
  | { readonly status: 'invalid'; readonly issue: SiteQualityIssue }

/**
 * Tolerance for the threshold comparison.
 *
 * Readings are entered to a tenth of a centimetre, but binary floating point
 * cannot represent those decimals exactly: `88.8 - 88.1` evaluates to
 * 0.7000000000000028, which a naive `> 0.7` would treat as exceeding the
 * threshold. That would warn about a spread that is arithmetically exactly at
 * it. The epsilon is far smaller than any real measurement difference, so it
 * cannot mask a genuine one.
 */
const COMPARISON_EPSILON = 1e-9

/**
 * The median of exactly three readings.
 *
 * With three values the median is the middle one after sorting — no averaging,
 * so the saved value is always a real reading the user actually took.
 */
export function medianOfThree(readings: readonly number[]): number {
  const sorted = [...readings].sort((left, right) => left - right)
  const middle = sorted[1]
  if (middle === undefined) throw new Error('medianOfThree needs exactly three readings')
  return middle
}

export function summariseSite(
  readings: readonly number[],
  thresholdCm: Centimetres = DEFAULT_SPREAD_THRESHOLD_CM,
): SiteQualityResult {
  if (readings.length !== READINGS_PER_SITE) {
    // A site is either skipped or fully recorded. There is no partial state
    // (D-045): a site measured once has unknown quality.
    return { status: 'invalid', issue: 'wrongReadingCount' }
  }
  if (readings.some((reading) => !Number.isFinite(reading))) {
    return { status: 'invalid', issue: 'nonFiniteReading' }
  }
  if (readings.some((reading) => reading <= 0)) {
    return { status: 'invalid', issue: 'nonPositiveReading' }
  }

  const sorted = [...readings].sort((left, right) => left - right)
  const lowest = sorted[0] as number
  const highest = sorted[sorted.length - 1] as number
  const spread = highest - lowest

  return {
    status: 'ok',
    quality: {
      readings: readings as readonly Centimetres[],
      medianCm: medianOfThree(readings) as Centimetres,
      spreadCm: spread as CentimetresDelta,
      exceedsThreshold: spread - (thresholdCm as number) > COMPARISON_EPSILON,
      thresholdUsedCm: thresholdCm,
    },
  }
}
